import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import NextLink from 'next/link';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { mediaQueries } from 'shared/styles';

/**
 * Validates that a banner link URL uses a safe scheme (http, https, or safe root-relative path).
 * Rejects dangerous schemes such as javascript:, vbscript:, data:, file:, etc.
 */
export function isSafeBannerLink(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }

  const sanitized = sanitizeUrl(trimmed);
  if (!sanitized || sanitized === 'about:blank') {
    return false;
  }

  // Allow http:// and https://
  if (/^https?:\/\//i.test(sanitized)) {
    return true;
  }

  // Allow safe root-relative paths (e.g. /stores/..., /specials/...)
  // Disallow protocol-relative (//) and backslash (/\) paths
  if (sanitized.startsWith('/') && !sanitized.startsWith('//') && !sanitized.startsWith('/\\')) {
    return true;
  }

  return false;
}

type BannerImageWrapperProps = {
  children: ReactNode;
  linkUrl: string | null | undefined;
  isInternalRoute?: boolean;
  target: string | null | undefined;
  rel: string | null | undefined;
  onClick: () => void;
};

export function BannerImageWrapper({
  children,
  linkUrl,
  isInternalRoute = false,
  target,
  rel,
  onClick,
}: BannerImageWrapperProps): JSX.Element {
  if (linkUrl && isSafeBannerLink(linkUrl)) {
    const safeUrl = sanitizeUrl(linkUrl.trim());

    // Use Next.js Link for internal routes (client-side navigation)
    if (isInternalRoute) {
      return (
        <NextLink href={safeUrl} passHref legacyBehavior>
          <InternalLink href={safeUrl} onClick={onClick}>
            {children}
          </InternalLink>
        </NextLink>
      );
    }

    // Use regular anchor for external links
    return (
      <ExternalLink href={safeUrl} target={target} rel={rel} onClick={onClick}>
        {children}
      </ExternalLink>
    );
  }

  return <Container>{children}</Container>;
}

const imageWrapperStyle = css`
  max-width: 1200px;
  width: 100%;
  margin: 0;
  border-radius: var(--dct--customized-radius-lg);
  @media ${mediaQueries.largePhone} {
    margin: 0 10px;
    padding: 0 16px;
  }
`;

const ExternalLink = styled.a`
  ${imageWrapperStyle}
`;

const InternalLink = styled.a`
  ${imageWrapperStyle}
`;

const Container = styled.div`
  ${imageWrapperStyle}
`;
